import { useEffect, useState, useRef, useCallback } from 'react'

import { TRACK_LOAD_BATCH } from '@disco/constants/ActionTypes'
import { TRACK } from '@disco/constants/ItemTypes'
import api from '@disco/api/server'

import type { ClientLibraryDiscoveryPanelItem } from 'types/client-library'
import type { PlaylistCurated } from 'types/playlist'
import type { Track } from 'types/track'

import { useDispatch } from 'hooks/use-redux'

import type { ContentType } from '../../../utils'

type FetchTracksResult = {
  tracks: Track[]
  isLoading: boolean
  error: string | null
}

/**
 * Generic hook for fetching tracks with loading/error state management.
 * Callers must stabilize fetchFn with useCallback to prevent unnecessary refetches.
 * Dispatches TRACK_LOAD_BATCH to Redux store when tracks are loaded.
 */
const useFetchTracks = (
  shouldFetch: boolean,
  fetchFn: () => Promise<Track[]>,
  dispatch: ReturnType<typeof useDispatch>,
): FetchTracksResult => {
  const [tracks, setTracks] = useState<Track[]>([])
  const [isLoading, setIsLoading] = useState(shouldFetch)
  const [error, setError] = useState<string | null>(null)
  const fetchIdRef = useRef(0)

  useEffect(() => {
    if (!shouldFetch) {
      setIsLoading(false)
      return
    }

    // Set loading synchronously to avoid flash of isLoading=false when shouldFetch transitions true
    setIsLoading(true)
    setError(null)

    const currentFetchId = ++fetchIdRef.current

    const loadTracks = async () => {
      try {
        const result = await fetchFn()

        if (currentFetchId !== fetchIdRef.current) return

        // Only dispatch to Redux store if we have tracks to add
        if (result.length > 0) {
          dispatch({ type: TRACK_LOAD_BATCH, list: result })
        }
        setTracks(result)
      } catch (err) {
        if (currentFetchId !== fetchIdRef.current) return
        console.error('Error loading tracks:', err)
        setError('Failed to load tracks.')
        setTracks([])
      } finally {
        if (currentFetchId === fetchIdRef.current) {
          setIsLoading(false)
        }
      }
    }

    loadTracks()
  }, [shouldFetch, fetchFn, dispatch])

  return { tracks, isLoading, error }
}

// API tracks are loosely typed; we add required fields for compatibility
const processApiTracks = (apiTracks: Record<string, unknown>[]): Track[] =>
  apiTracks.map(track => ({
    ...track,
    type: TRACK,
  })) as Track[]

const CATALOG_DETAIL_TRACKS_COUNT = 12

export const useGetCatalogTracks = (
  catalog: ClientLibraryDiscoveryPanelItem | null,
): FetchTracksResult => {
  const dispatch = useDispatch()
  const catalogId = catalog?.id

  const fetchFn = useCallback(async (): Promise<Track[]> => {
    const result = await api.getClientLibraryDiscoveryPanelById(
      catalogId!,
      CATALOG_DETAIL_TRACKS_COUNT,
    )
    return processApiTracks(result?.featured_tracks || [])
  }, [catalogId])

  return useFetchTracks(!!catalogId, fetchFn, dispatch)
}

export const useGetPlaylistTracks = (
  playlist: PlaylistCurated | null,
): FetchTracksResult => {
  const dispatch = useDispatch()
  const playlistId = playlist?.id

  const fetchFn = useCallback(async (): Promise<Track[]> => {
    const result = await api.clientLibraryCuratedPlaylistTracks(
      Number(playlistId),
    )
    return processApiTracks(result?.tracks || [])
  }, [playlistId])

  return useFetchTracks(!!playlistId, fetchFn, dispatch)
}

export const useGetContentTracks = (
  contentType: ContentType,
  playlist: PlaylistCurated | null,
  catalog: ClientLibraryDiscoveryPanelItem | null,
) => {
  // Both hooks run (React rules), but only the active one fetches (null prevents API calls)
  const playlistResult = useGetPlaylistTracks(
    contentType === 'playlist' ? playlist : null,
  )
  const catalogResult = useGetCatalogTracks(
    contentType === 'catalog' ? catalog : null,
  )

  return contentType === 'playlist' ? playlistResult : catalogResult
}
